import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";

import { getAdminBrands, getAdminCategories, getAdminProducts } from "@/lib/admin-api-client";
import { countCategories } from "@/components/admin/adminCategoryTree";

/**
 * `/admin/catalogue` — équivalent de apps/web/app/(admin)/admin/catalogue/page.tsx :
 * page d'accueil du domaine catalogue, trois cartes de comptage (chacune un
 * lien vers sa propre sous-page).
 */
export function AdminCataloguePage() {
  const productsQuery = useQuery({
    queryKey: ["admin", "products", { page: 1, pageSize: 1 }],
    queryFn: () => getAdminProducts(undefined, { page: 1, pageSize: 1 }),
  });
  const categoriesQuery = useQuery({
    queryKey: ["admin", "categories"],
    queryFn: () => getAdminCategories(),
  });
  const brandsQuery = useQuery({
    queryKey: ["admin", "brands"],
    queryFn: () => getAdminBrands(),
  });

  const isLoading = productsQuery.isLoading || categoriesQuery.isLoading || brandsQuery.isLoading;

  return (
    <div>
      <h1 className="font-display text-2xl font-semibold text-ink">Catalogue</h1>
      <p className="mt-1 text-sm text-muted">Produits, catégories et marques de la boutique.</p>

      {isLoading ? (
        <p className="mt-6 text-sm text-muted">Chargement…</p>
      ) : (
        <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-3">
          <CatalogueCard
            href="/admin/catalogue/produits"
            label="Produits"
            value={productsQuery.data ? productsQuery.data.totalItems.toString() : "—"}
            cta="Gérer les produits →"
          />
          <CatalogueCard
            href="/admin/catalogue/categories"
            label="Catégories"
            value={categoriesQuery.data ? countCategories(categoriesQuery.data).toString() : "—"}
            cta="Gérer les catégories →"
          />
          <CatalogueCard
            href="/admin/catalogue/marques"
            label="Marques"
            value={brandsQuery.data ? brandsQuery.data.length.toString() : "—"}
            cta="Gérer les marques →"
          />
        </div>
      )}
    </div>
  );
}

function CatalogueCard({ href, label, value, cta }: { href: string; label: string; value: string; cta: string }) {
  return (
    <Link to={href} className="block">
      <div className="flex h-full flex-col justify-between rounded-sm border border-line bg-white p-5 shadow-sm transition-colors hover:border-navy">
        <p className="text-sm font-medium text-muted">{label}</p>
        <p className="mt-3 font-display text-3xl font-semibold text-ink">{value}</p>
        <p className="mt-2 text-sm font-medium text-navy">{cta}</p>
      </div>
    </Link>
  );
}
